// GiftCategory.jsx
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useInView } from 'react-intersection-observer';

import {
  InfiniteScroller,
  ScrollItem,
} from '../component/InfiniteScroller.jsx';

import GiftPreviewContext from '../context/GiftPreviewContext.jsx';
import Gift from '../container/Gift.js';
import LiveStreamGift from '../container/LiveStreamGift.js';
import LiveStreamCupidGiftEntry from '../container/LiveStreamCupidGiftEntry.js';

import { EMPTY_ARRAY } from '../resource/defaults.js';
import { Game } from '../resource/gameConstants.js';

import LoadingImage from '../../img/img-loading-dark-20.svg';

const CUPID_GIFT_ENTRY_META = { id: Game.UGPP_CUPID_GIFT };

export class GiftCategory extends React.PureComponent {
  static contextType = GiftPreviewContext;

  state = {
    isIntersected: false,
  };

  componentDidMount() {
    const { category, isFetched, isFirstPageFetching, fetchGifts } = this.props;
    const { isIntersected } = this.state;

    if (isIntersected && category && !isFetched && !isFirstPageFetching) {
      fetchGifts({ category, page: 1 });
    }
    this.selectFirstGift();
  }

  componentDidUpdate(prevProps, prevState) {
    const { category, isFetched, isFirstPageFetching, fetchGifts } = this.props;
    const { isIntersected } = this.state;

    if (
      isIntersected !== prevState.isIntersected ||
      isFetched !== prevProps.isFetched ||
      isFirstPageFetching !== prevProps.isFirstPageFetching ||
      category !== prevProps.category
    ) {
      if (isIntersected && category && !isFetched && !isFirstPageFetching) {
        fetchGifts({ category, page: 1 });
      }
    }

    const hasBecomeActive = this.props.isActive && !prevProps.isActive;
    const hasFirstGiftLoaded =
      !prevProps.firstGiftMeta && this.props.firstGiftMeta;
    if (hasBecomeActive || (this.props.isActive && hasFirstGiftLoaded)) {
      this.selectFirstGift();
    }
  }

  selectCupidGiftEntry = () => {
    this.context?.selectGift(CUPID_GIFT_ENTRY_META, {
      shouldShowPreview: false,
    });
  };

  selectFirstGift = () => {
    if (!this.context) return;
    const { isActive, firstGiftMeta, shouldShowCupidGiftEntry } = this.props;
    if (!isActive) return;
    if (shouldShowCupidGiftEntry) {
      return this.selectCupidGiftEntry();
    }
    if (!firstGiftMeta) return;
    this.context.selectGift(firstGiftMeta, { shouldShowPreview: false });
  };

  handleIntersection = ({ isIntersected }) => {
    this.setState({ isIntersected });
  };

  renderGift = id => {
    const { userId, type, chatroomId, isActive } = this.props;
    if (type === 'chatroom') {
      return (
        <GiftWrapper key={id}>
          <Gift id={id} type="panel" userId={userId} chatroomId={chatroomId} />
        </GiftWrapper>
      );
    }
    if (type === 'livestream') {
      return (
        <GiftWrapper key={id}>
          <LiveStreamGift
            id={id}
            type="livestream"
            streamId={userId}
            isActive={isActive}
          />
        </GiftWrapper>
      );
    }
  };

  renderLoader = () => {
    return (
      <LoaderWrapper>
        <Loader src={LoadingImage} />
      </LoaderWrapper>
    );
  };
  render() {
    const {
      itemIds,
      category,
      nextPage,
      isFirstPageFetching,
      isNextPageFetching,
      fetchGifts,
      slideHeight,
      userId,
      shouldShowCupidGiftEntry,
    } = this.props;
    return (
      <ObservableGiftSlide
        slideHeight={slideHeight}
        onChange={this.handleIntersection}
      >
        <InfiniteScroller
          useWindow={false}
          pageStart={1}
          hasMore={nextPage != null}
          loadMore={() => fetchGifts({ category, page: nextPage })}
          isFetching={isNextPageFetching}
          threshold={100}
        >
          <GiftList>
            {shouldShowCupidGiftEntry && (
              <GiftWrapper>
                <LiveStreamCupidGiftEntry
                  streamId={userId}
                  isSelected={
                    this.context?.selectedGift?.id === Game.UGPP_CUPID_GIFT
                  }
                  onSelect={this.selectCupidGiftEntry}
                />
              </GiftWrapper>
            )}
            {isFirstPageFetching && itemIds.length === 0 && this.renderLoader()}
            <ScrollItem loader={this.renderLoader()}>
              {this.state.isIntersected
                ? itemIds.map(this.renderGift)
                : EMPTY_ARRAY}
            </ScrollItem>
          </GiftList>
        </InfiniteScroller>
      </ObservableGiftSlide>
    );
  }
}

GiftCategory.propTypes = {
  userId: PropTypes.string,
  chatroomId: PropTypes.string,
  category: PropTypes.string,
  itemIds: PropTypes.array,
  nextPage: PropTypes.number,
  isFetched: PropTypes.bool,
  isFirstPageFetching: PropTypes.bool,
  isNextPageFetching: PropTypes.bool,
  isActive: PropTypes.bool,
  fetchGifts: PropTypes.func,
  type: PropTypes.string,
  slideHeight: PropTypes.number,
  firstGiftMeta: PropTypes.object,
  shouldShowCupidGiftEntry: PropTypes.bool,
};

GiftCategory.defaultProps = {
  userId: '',
  chatroomId: '',
  category: '',
  itemIds: EMPTY_ARRAY,
  nextPage: null,
  isFetched: false,
  isFirstPageFetching: false,
  isNextPageFetching: null,
  isActive: false,
  fetchGifts: () => null,
  type: 'chatroom',
  slideHeight: 200,
  firstGiftMeta: null,
  shouldShowCupidGiftEntry: false,
};

const OBSERVER_OPTIONS = {
  threshold: 0.01,
  triggerOnce: true,
};

const ObservableGiftSlide = ({ onChange, slideHeight, children }) => {
  const { ref, inView } = useInView(OBSERVER_OPTIONS);

  useEffect(() => {
    if (inView) {
      onChange({ isIntersected: true });
    }
  }, [inView, onChange]);

  return (
    <StyledGiftSlide ref={ref} height={slideHeight}>
      {children}
    </StyledGiftSlide>
  );
};

ObservableGiftSlide.propTypes = {
  onChange: PropTypes.func,
  slideHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  children: PropTypes.node,
};

const StyledGiftSlide = styled.div.attrs(({ height }) => ({
  style: {
    height: `${height}px`,
  },
}))`
  padding: 8px 16px 32px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const GiftList = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
`;

const GiftWrapper = styled.div`
  margin: 5px 0;
  width: 25%;
  text-align: center;
  display: flex;
  justify-content: center;
`;

const LoaderWrapper = styled.div`
  width: 100%;
  padding-top: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const Loader = styled.img`
  width: 32px;
  height: 32px;
`;

export default GiftCategory;
